import React, { useCallback, useEffect, useContext, useState } from "react";
import NavListItem from "../NavListItem";
import HeaderLink from "../HeaderLink";
import isMobile from "@src/utils/isMobile";
import Translate from "@src/components/translation/Translate";
import { FlexProps } from "@src/components/Boxes";
import DownloadAppButton from "../DownloadAppButton";
import LoginPart from "../LoginPart";
import * as S from "./styles";
import { debounce, isNil } from "lodash";
import { TranslationContext } from "@src/components/translation/TranslationContext";
import { HeaderHTMLA } from "../HeaderLink/styles";
import { logEvent } from "@src/utils/logClient";
import { get } from "cookies";
import { path } from "lodash/fp";
import { getRelativePath } from "@src/utils/routes";
import styled from "@emotion/styled";
import { mediaQueries, mediaQueriesMax } from "@src/styles/theme";
import { Link } from "gatsby";
import { ListItem } from "../NavListItem";
import { List } from "vendor/classdojo-web/dist";

// Styled component for consistent list item styling
const SimpleListItem = styled("li")`
  padding: 10px 15px;
  list-style: none;
  margin-top: 0;
  ${mediaQueriesMax[2]} {
    width: 100%;
  }
  ${mediaQueries[2]} {
    padding: 0;
  }
`;

type NavLinkProps = FlexProps & {
  openLoginModal: () => void;
  openSignupModal: () => void;
  closeMenu: () => void;
  active?: boolean;
  mobile?: boolean;
};

const NavLinks: React.FC<NavLinkProps> = ({ openLoginModal, openSignupModal, closeMenu, ...props }) => {
  const [downloadButtonConfig, setDownloadButtonConfig] = useState<{
    href: string;
    translationPath: string;
  } | null>(null);

  const handleOpenLogin = useCallback(() => {
    closeMenu();
    openLoginModal();
    logEvent({
      eventValue: window.location.href,
      eventName: "web.external_page.navbar.login.tap",
    });
  }, [closeMenu, openLoginModal]);

  const handleOpenSignup = useCallback(() => {
    closeMenu();
    openSignupModal();
    logEvent({
      eventValue: window.location.href,
      eventName: "web.external_page.navbar.signup.tap",
    });
  }, [closeMenu, openSignupModal]);

  const { getIsCountryUS } = useContext(TranslationContext);

  const isCountryUS = getIsCountryUS();

  // Helper function to handle clicks for simple links
  const handleLinkClick = useCallback(
    (eventName: string) => {
      return () => {
        closeMenu();
        logEvent({
          eventValue: window.location.href,
          eventName,
        });
      };
    },
    [closeMenu],
  );

  // This is necessary so the focus doesn't stay trapped in the nav
  // if the menu is open on small screen and the user resizes it to a bigger width
  useEffect(() => {
    const debouncedHandleResize = debounce(function handleResize() {
      closeMenu();
    }, 1000);

    window.addEventListener("resize", debouncedHandleResize);

    return () => {
      window.removeEventListener("resize", debouncedHandleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect mobile device and set download button config on client-side only
  // This prevents SSR hydration mismatch errors
  useEffect(() => {
    if (isMobile().apple.tablet) {
      setDownloadButtonConfig({
        href: "https://itunes.apple.com/us/app/classdojo/id552602056?mt=8",
        translationPath: "header.nav.get_ipad_app",
      });
    } else if (isMobile().apple.phone) {
      setDownloadButtonConfig({
        href: "https://itunes.apple.com/us/app/classdojo/id552602056?mt=8",
        translationPath: "header.nav.get_iphone_app",
      });
    } else if (isMobile().android.device) {
      setDownloadButtonConfig({
        href: "https://play.google.com/store/apps/details?id=com.classdojo.android",
        translationPath: "header.nav.get_android_app",
      });
    }
  }, []);

  const handleKeyDown = (event: { key: string; preventDefault: () => void; stopPropagation: () => void }) => {
    if (event.key === "Escape") closeMenu();
    const currentListItem = document.activeElement?.parentElement as HTMLLIElement;
    const parentList = currentListItem.parentNode;
    const previousElement = currentListItem.previousElementSibling;
    const nextElement = currentListItem.nextElementSibling;

    let targetListItem;
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (previousElement) targetListItem = previousElement;
      else targetListItem = parentList?.lastElementChild;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (nextElement) targetListItem = nextElement;
      else targetListItem = parentList?.firstElementChild;
    }
    if (targetListItem) (targetListItem.firstElementChild as HTMLElement)?.focus();
    event.stopPropagation();
  };

  return (
    <>
      <S.NavLinksContainer active={props.active} aria-hidden={!props.active} onKeyDown={handleKeyDown}>
        <div />
        <S.NavList>
          {isCountryUS && (
            <NavListItem
              translationPath="layouts.main.products"
              path=""
              onClick={closeMenu}
              eventName="web.external_page.navbar.products.tap"
              sublinks={[
                {
                  eventName: "web.external_page.navbar.products.classdojo.tap",
                  ctaTranslated: "layouts.main.submenu_products_classdojo.cta",
                  descriptionTranslated: "layouts.main.submenu_products_classdojo.description",
                  path: "/?utm_source=newnav",
                },
                {
                  eventName: "web.external_page.navbar.products.plus.tap",
                  ctaTranslated: "layouts.main.submenu_products_plus.cta",
                  descriptionTranslated: "layouts.main.submenu_products_plus.description",
                  path: "/plus/?utm_source=newnav",
                },
                {
                  eventName: "web.external_page.navbar.products.islands.tap",
                  ctaTranslated: "layouts.main.submenu_products_dojo_islands.cta",
                  descriptionTranslated: "layouts.main.submenu_products_dojo_islands.description",
                  path: "/dojo-islands?utm_source=newnav",
                },
                {
                  eventName: "web.external_page.navbar.products.tutor.tap",
                  ctaTranslated: "layouts.main.submenu_products_dojo_tutor.cta",
                  descriptionTranslated: "layouts.main.submenu_products_dojo_tutor.description",
                  path: "https://tutor.classdojo.com/?utm_source=newnav",
                },
                {
                  eventName: "web.external_page.navbar.products.sparks.tap",
                  ctaTranslated: "layouts.main.submenu_products_dojo_sparks.cta",
                  descriptionTranslated: "layouts.main.submenu_products_dojo_sparks.description",
                  path: "/sparks?utm_source=newnav",
                },
              ]}
            >
              <S.LinkText>
                <Translate path="layouts.main.products" />
              </S.LinkText>
            </NavListItem>
          )}

          {/* Schools - with dropdown */}
          {isCountryUS && (
            <NavListItem
              translationPath="layouts.main.schools"
              path=""
              onClick={closeMenu}
              eventName="web.external_page.navbar.schools.tap"
              sublinks={[
                {
                  eventName: "web.external_page.navbar.schools.teachers.tap",
                  ctaTranslated: "layouts.main.submenu_schools_teachers.cta",
                  descriptionTranslated: "layouts.main.submenu_schools_teachers.description",
                  path: "/teachers/?utm_source=newnav",
                },
                {
                  eventName: "web.external_page.navbar.schools.leaders.tap",
                  ctaTranslated: "layouts.main.submenu_schools_leaders.cta",
                  descriptionTranslated: "layouts.main.submenu_schools_leaders.description",
                  path: "/schools/?utm_source=newnav",
                },
                {
                  eventName: "web.external_page.navbar.schools.resources.tap",
                  ctaTranslated: "layouts.main.submenu_schools_resources.cta",
                  descriptionTranslated: "layouts.main.submenu_schools_resources.description",
                  path: "/resources/?utm_source=newnav",
                },
              ]}
            >
              <S.LinkText>
                <Translate path="layouts.main.schools" />
              </S.LinkText>
            </NavListItem>
          )}

          {isCountryUS && (
            <NavListItem
              translationPath="layouts.main.districts"
              path=""
              onClick={closeMenu}
              eventName="web.external_page.navbar.districts.tap"
              sublinks={[
                {
                  eventName: "web.external_page.navbar.districts.overview.tap",
                  ctaTranslated: "layouts.main.submenu_districts_overview.cta",
                  descriptionTranslated: "layouts.main.submenu_districts_overview.description",
                  path: "/districts/?utm_source=newnav",
                },
                {
                  eventName: "web.external_page.navbar.districts.roles.tap",
                  ctaTranslated: "layouts.main.submenu_districts_roles.cta",
                  descriptionTranslated: "layouts.main.submenu_districts_roles.description",
                  path: "/districts/roles/?utm_source=newnav",
                },
                {
                  eventName: "web.external_page.navbar.districts.features.tap",
                  ctaTranslated: "layouts.main.submenu_districts_features.cta",
                  descriptionTranslated: "layouts.main.submenu_districts_features.description",
                  path: "/districts/solutions/?utm_source=newnav",
                },
                {
                  eventName: "web.external_page.navbar.districts.privacy.tap",
                  ctaTranslated: "layouts.main.submenu_districts_privacy.cta",
                  descriptionTranslated: "layouts.main.submenu_districts_privacy.description",
                  path: "/privacy-and-security/?utm_source=newnav",
                },
                {
                  eventName: "web.external_page.navbar.districts.stories.tap",
                  ctaTranslated: "layouts.main.submenu_districts_stories.cta",
                  descriptionTranslated: "layouts.main.submenu_districts_stories.description",
                  path: "/districts/customer-stories/?utm_source=newnav",
                },
                {
                  eventName: "web.external_page.navbar.districts.blog.tap",
                  ctaTranslated: "layouts.main.submenu_districts_blog.cta",
                  descriptionTranslated: "layouts.main.submenu_districts_blog.description",
                  path: "https://essential.classdojo.com/?utm_source=newnav",
                },
              ]}
            ></NavListItem>
          )}

          {isCountryUS && (
            <ListItem>
              <Link to="/families/" onClick={handleLinkClick("web.external_page.navbar.families.tap")}>
                <S.LinkText>
                  <Translate path="layouts.main.families" />
                </S.LinkText>
              </Link>
            </ListItem>
          )}
          {!isCountryUS && (
            <>
              <ListItem>
                <Link
                  to={getRelativePath("/teachers/")}
                  onClick={handleLinkClick("web.external_page.navbar.teachers.tap")}
                >
                  <S.LinkText>
                    <Translate path="layouts.main.teachers" />
                  </S.LinkText>
                </Link>
              </ListItem>

              <ListItem>
                <Link
                  to={getRelativePath("/schools/")}
                  onClick={handleLinkClick("web.external_page.navbar.schools.tap")}
                >
                  <S.LinkText>
                    <Translate path="layouts.main.schools" />
                  </S.LinkText>
                </Link>
              </ListItem>
              <NavListItem
                path=""
                translationPath="layouts.main.support.resources"
                onClick={closeMenu}
                eventName="web.external_page.navbar.resources.tap"
                sublinks={[
                  {
                    ctaTranslated: "layouts.main.submenu_training.cta",
                    descriptionTranslated: "layouts.main.submenu_training.description",
                    path: "/training/?utm_source=newnav",
                    eventName: "web.external_page.navbar.training.tap",
                  },
                  {
                    ctaTranslated: "layouts.main.submenu_resources.cta",
                    descriptionTranslated: "layouts.main.submenu_resources.description",
                    path: "/resources/?utm_source=newnav",
                    eventName: "web.external_page.navbar.resources.tap",
                  },
                  {
                    ctaTranslated: "layouts.main.submenu_big_ideas.cta",
                    descriptionTranslated: "layouts.main.submenu_big_ideas.description",
                    path: "https://ideas.classdojo.com/?utm_source=newnav",
                    eventName: "web.external_page.navbar.big_ideas.tap",
                  },
                  {
                    ctaTranslated: "layouts.main.submenu_activity_corner.cta",
                    descriptionTranslated: "layouts.main.submenu_activity_corner.description",
                    path: "/activity-corner/?utm_source=newnav",
                    eventName: "web.external_page.navbar.activity_corner.tap",
                  },
                  {
                    ctaTranslated: "layouts.main.submenu_sl_resources.cta",
                    descriptionTranslated: "layouts.main.submenu_sl_resources.description",
                    path: "/schoolleader-resources/?utm_source=newnav",
                    eventName: "web.external_page.navbar.schoolleader_resources.tap",
                  },
                ]}
              >
                <S.LinkText>
                  <Translate path="layouts.main.support.resources" />
                </S.LinkText>
              </NavListItem>
            </>
          )}
        </S.NavList>

        <S.NavList>
          <LoginPart openLoginModal={handleOpenLogin} openSignupModal={handleOpenSignup} />
          {downloadButtonConfig && (
            <DownloadAppButton
              buttonHref={downloadButtonConfig.href}
              translationPath={downloadButtonConfig.translationPath}
            />
          )}
        </S.NavList>
      </S.NavLinksContainer>
    </>
  );
};

export default NavLinks;
